import React, { useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import scriptLoader from 'react-async-script-loader'
import config from 'config'
import { useForm } from 'react-hook-form'

import { required } from '@cred/shared/validations'

import { PERSONAL_FILTER_FORM_INITIAL_VALUES as initialValues } from 'common/constants'
import PersonalFilterForm from 'components/filter-form'
import LendersList from 'components/lenders-list'
import { fireGtmEvent } from 'utils/gtm'
import { getCtaUrl } from 'personal-loan/rate-table/utils'
import { loadLendersRates, filterLender } from 'common/utils/rates'

import { minMaxLoanAmountValidator, useLendersSummary } from '../../validations'
import styles from './styles.module.scss'

const FullRateTableApp = ({
  theme,
  isScriptLoaded,
  widgetContext,
  filterFormClassName,
  lenderListClassName,
  availableLenders = [],
  embeddedWidgetQueryParams,
  otherOptions,
}) => {
  const { watch, control, formState, reset } = useForm({
    defaultValues: initialValues,
    mode: 'onChange',
  })
  const [loanAmount, creditScoreApprox] = watch(['loanAmount', 'creditScoreApprox'])

  useEffect(() => {
    fireGtmEvent('fieldChange', { loanAmount, ...widgetContext })
  }, [loanAmount])

  useEffect(() => {
    fireGtmEvent('fieldChange', { creditScore: parseInt(creditScoreApprox, 10), ...widgetContext })
  }, [creditScoreApprox])

  const lenders = useMemo(() => {
    if (isScriptLoaded) {
      return loadLendersRates({
        availableLenders: filterLender(availableLenders, creditScoreApprox, loanAmount),
        filters: { vertical: 'personal' },
      })
    }

    return []
  }, [isScriptLoaded, availableLenders, loanAmount, creditScoreApprox])

  const { minLoanAmount, maxLoanAmount } = useLendersSummary()

  const minMaxLoanAmountValidation = useCallback(
    (value) => minMaxLoanAmountValidator(minLoanAmount, maxLoanAmount)(value),
    [minLoanAmount, maxLoanAmount],
  )

  const ctaUrl = getCtaUrl({ embeddedWidgetQueryParams })

  return (
    <div className={styles.defaultContainer}>
      <PersonalFilterForm
        control={control}
        customValidation={[required, minMaxLoanAmountValidation]}
        className={`${filterFormClassName} ${styles.defaultFilters}`}
        invalid={formState.isDirty && !formState.isValid}
      />
      <hr />
      <LendersList
        lenders={lenders}
        ctaUrl={ctaUrl}
        resetFilters={() => reset(initialValues)}
        theme={theme}
        name="personalLoan"
        className={`${lenderListClassName} ${styles.productList}`}
        otherOptions={otherOptions}
      />
    </div>
  )
}

FullRateTableApp.propTypes = {
  isScriptLoaded: PropTypes.bool,
  embeddedWidgetQueryParams: PropTypes.string.isRequired,
  widgetContext: PropTypes.object,
  theme: PropTypes.string,
  filterFormClassName: PropTypes.string,
  lenderListClassName: PropTypes.string,
  availableLenders: PropTypes.instanceOf(Array),
  otherOptions: PropTypes.string,
}

// Export named class and functions for unit tests
export { FullRateTableApp }

export default scriptLoader(config.ratesLibraryUrl)(FullRateTableApp)
